import { StrictMode } from 'react';
import './i18n/index.ts';
import { createRoot } from 'react-dom/client';
import { setActiveApp, getRuntimeConfig } from '@quickbee/api-config';
import { initAuthSession } from '@quickbee/auth/internal';
import { hydrateTenantFromCache, initTenant, sessionOptions } from '@/lib/tenant';
import { assertThemeWiring } from '@/theme';
import './index.css';
import App from './App.tsx';

// Bootstrap order matters (design doc §4.3): the app name feeds the
// single-active-session slot on every request, so it must be set before the
// first client is constructed. The backend's slot for this persona is
// `customer`, not `pwa`.
setActiveApp('customer');

// Reading the config here surfaces a malformed or absent injection at boot
// rather than as a failing request mid-session.
getRuntimeConfig();

// The host's brand from the last visit, in the store before React's first render.
// `initTenant` below resolves the real one, but it is a network round trip and the
// render is NOT waiting for it — without this seed the logo and the tenant's name
// are absent for its whole duration and then appear, which is the flash from the
// saver's side. The blocking script in index.html has already replayed the palette
// from the same cache; this is the half only React can draw.
hydrateTenantFromCache();

void (async () => {
  // Host-resolved tenant first: the refresh call (and every auth mutation)
  // must carry X-Tenant-ID or TenantResolutionFilter refuses it.
  await initTenant();

  // Silent bootstrap: sends the HttpOnly refresh cookie; on success hydrates the
  // store and schedules the proactive refresh cycle. The splash screen is the
  // visual cover for this. App-lifetime session — the disposer is unused.
  //
  // Always attempted: `initAuthSession` settles the store to unauthenticated by
  // itself when nothing is restored, so there is no case that needs a shortcut
  // around it.
  //
  // The idle policy is deliberately looser than the shell's 30 minutes: this is a
  // saver's own phone, not a shared counter terminal, and an hour without a tap is
  // ordinary use of a savings app rather than an abandoned session. It is still
  // bounded — a lost phone should not hold a live session indefinitely.
  void initAuthSession(sessionOptions(), {
    idle: { timeoutMs: 60 * 60_000, warningMs: 2 * 60_000 }
  });
})();

// Dev-only: catches a theme colour that exists as a variable but not as a
// generated utility class — the silent "half the chrome vanished" failure.
assertThemeWiring();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
